/**
 * Curated industry groups for Lead Generation focus. Aligned with the seller
 * intake `industry` question so qualification stays measurable, and mapped to
 * copy vocabulary, imagery hints, and Meta targeting discovery queries.
 */

export type LeadGenIndustryGroup = {
  key: string;
  label: string;
  /** Option key used by the seller intake `industry` question. */
  intakeKey: string;
  /** Intake option label shown to sellers. */
  intakeLabel: string;
  /** Plain-language vocabulary the copywriter may use. */
  vocabulary: string[];
  /** Photographic settings the creative director may use. */
  imagery: string[];
  /** Queries used to discover Meta targeting options for this group. */
  searchTerms: string[];
};

export const LEAD_GEN_INDUSTRY_GROUPS: readonly LeadGenIndustryGroup[] = [
  {
    key: "home_services_trades",
    label: "Trades & home services",
    intakeKey: "home_services",
    intakeLabel:
      "Home services / trades (HVAC, plumbing, electrical, contracting)",
    vocabulary: ["crews", "trucks", "service contracts", "route density", "technicians"],
    imagery: ["service van and crew at a job site", "HVAC rooftop units", "workshop bay"],
    searchTerms: [
      "HVAC",
      "plumbing",
      "roofing contractor",
      "electrical contractor",
      "landscaping",
      "home services",
      "pest control",
      "pool service",
    ],
  },
  {
    key: "professional_services",
    label: "Professional services",
    intakeKey: "professional_services",
    intakeLabel: "Professional services (accounting, insurance, law, marketing)",
    vocabulary: ["recurring clients", "book of business", "partners", "retainers"],
    imagery: ["modern office with team", "client meeting room", "downtown professional district"],
    searchTerms: [
      "accounting firm",
      "insurance agency",
      "law firm",
      "marketing agency",
      "consulting firm",
      "staffing agency",
    ],
  },
  {
    key: "manufacturing_industrial",
    label: "Manufacturing & industrial",
    intakeKey: "manufacturing_industrial",
    intakeLabel: "Manufacturing or industrial",
    vocabulary: ["plant", "production line", "capacity", "long-term customers"],
    imagery: ["factory floor", "machine shop", "industrial park exterior"],
    searchTerms: ["manufacturing", "machine shop", "metal fabrication", "industrial equipment"],
  },
  {
    key: "distribution_logistics",
    label: "Distribution & logistics",
    intakeKey: "distribution_logistics",
    intakeLabel: "Distribution or logistics",
    vocabulary: ["fleet", "routes", "warehouse", "supplier relationships"],
    imagery: ["warehouse loading dock", "trucking yard", "distribution center"],
    searchTerms: ["logistics", "trucking company", "wholesale distribution", "freight"],
  },
  {
    key: "healthcare_support",
    label: "Healthcare support & clinics",
    intakeKey: "healthcare_support",
    intakeLabel: "Healthcare-support or clinic network",
    vocabulary: ["patients", "providers", "locations", "referral network"],
    imagery: ["clinic reception", "medical office exterior", "dental practice"],
    searchTerms: ["medical practice", "dental practice", "home health care", "physical therapy"],
  },
  {
    key: "construction_development",
    label: "Construction & development",
    intakeKey: "construction_development",
    intakeLabel: "Construction or development",
    vocabulary: ["backlog", "bonded projects", "subcontractors", "general contracting"],
    imagery: ["construction site with cranes", "commercial build-out", "site trailer"],
    searchTerms: ["general contractor", "construction", "commercial construction", "concrete contractor"],
  },
  {
    key: "real_estate_property_management",
    label: "Real estate & property management",
    intakeKey: "real_estate_property_management",
    intakeLabel: "Real estate or property management",
    vocabulary: ["doors under management", "portfolio", "occupancy", "owners"],
    imagery: ["apartment community", "property office", "commercial plaza"],
    searchTerms: ["property management", "real estate brokerage", "commercial real estate"],
  },
  {
    key: "hospitality_food_service",
    label: "Hospitality & food service",
    intakeKey: "specialty_hospitality_food_service",
    intakeLabel: "Specialty hospitality or food service group",
    vocabulary: ["locations", "units", "guest traffic", "franchise"],
    imagery: ["restaurant kitchen line", "hotel lobby", "cafe storefront"],
    searchTerms: ["restaurant owner", "franchise", "hospitality industry", "catering"],
  },
  {
    key: "automotive_transport",
    label: "Automotive & transport",
    intakeKey: "automotive_transport",
    intakeLabel: "Automotive, repair, or transportation",
    vocabulary: ["bays", "fleet accounts", "repeat customers", "locations"],
    imagery: ["auto repair shop", "dealership lot", "car wash tunnel"],
    searchTerms: ["auto repair", "car wash", "automotive dealership", "collision repair"],
  },
  {
    key: "retail_consumer_services",
    label: "Retail & consumer services",
    intakeKey: "retail_consumer_services",
    intakeLabel: "Retail, salons, or other consumer services",
    vocabulary: ["locations", "foot traffic", "loyal customers", "staff"],
    imagery: ["storefront on a busy street", "boutique interior", "salon floor"],
    searchTerms: ["retail business", "salon owner", "boutique", "franchise owner"],
  },
  {
    key: "technology",
    label: "Technology & software",
    intakeKey: "technology",
    intakeLabel: "Technology, software, or IT services",
    vocabulary: ["recurring revenue", "clients", "engineers", "platform"],
    imagery: ["modern tech office", "server room", "team at whiteboards"],
    searchTerms: ["software company", "IT services", "managed service provider", "SaaS"],
  },
  {
    key: "other",
    label: "Other",
    intakeKey: "other",
    intakeLabel: "Other",
    vocabulary: [],
    imagery: [],
    searchTerms: [],
  },
];

/**
 * Marketplace `IndustryCategory.slug` → lead-gen group. Business type slugs
 * override the category when a category mixes trades and professional work.
 */
const MARKETPLACE_CATEGORY_TO_GROUP: Record<string, string> = {
  hospitality: "hospitality_food_service",
  "food-beverage": "hospitality_food_service",
  "entertainment-and-recreation": "hospitality_food_service",
  "beauty-personal-care": "retail_consumer_services",
  retail: "retail_consumer_services",
  "pet-services": "retail_consumer_services",
  "education-and-children": "retail_consumer_services",
  "automotive-boat": "automotive_transport",
  "transportation-and-storage": "distribution_logistics",
  "wholesale-and-distributors": "distribution_logistics",
  "building-construction": "construction_development",
  "service-business": "professional_services",
  "financial-services": "professional_services",
  manufacturing: "manufacturing_industrial",
  "health-care-fitness": "healthcare_support",
  technology: "technology",
  agriculture: "other",
  "communication-and-media": "other",
  other: "other",
};

const MARKETPLACE_BUSINESS_TYPE_TO_GROUP: Record<string, string> = {
  "hvac-service": "home_services_trades",
  "electrical-service": "home_services_trades",
  "roofing-contractor": "home_services_trades",
  "plumbing-service": "home_services_trades",
  "painting-contractor": "home_services_trades",
  "flooring-contractor": "home_services_trades",
  "drywall-contractor": "home_services_trades",
  "kitchen-bath-remodeling-contractor": "home_services_trades",
  "handyman-services": "home_services_trades",
  "appliance-repair-service": "home_services_trades",
  "pool-service-repair": "home_services_trades",
  "landscaping-service": "home_services_trades",
  "pest-control-service": "home_services_trades",
  "cleaning-service": "home_services_trades",
  "commercial-real-estate-brokerage": "real_estate_property_management",
  "vacation-rental-property-management": "real_estate_property_management",
  "property-management": "real_estate_property_management",
};

/** Accepts a marketplace category slug or display name ("Food & Beverage"). */
export function normalizeMarketplaceCategorySlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function resolveLeadGenIndustryGroupFromMarketplace(input: {
  categorySlug?: string | null;
  businessTypeSlug?: string | null;
}): LeadGenIndustryGroup | undefined {
  const byType = input.businessTypeSlug
    ? MARKETPLACE_BUSINESS_TYPE_TO_GROUP[input.businessTypeSlug]
    : undefined;
  const byCategory = input.categorySlug
    ? MARKETPLACE_CATEGORY_TO_GROUP[normalizeMarketplaceCategorySlug(input.categorySlug)]
    : undefined;
  const key = byType ?? byCategory;
  return key ? industryByKey.get(key) : undefined;
}

/**
 * Parse an intake `annual_revenue` answer (option slug, legacy lead-form key,
 * or label) into a cents band. Returns null when the answer is not a band.
 */
export function parseLeadGenIntakeRevenueBand(
  value: unknown,
): { minCents: number | null; maxCents: number | null } | null {
  if (typeof value !== "string") return null;
  const raw = value.trim().toLowerCase();
  if (!raw || raw.includes("discuss")) return null;
  const normalized = raw
    .replace(/\$/g, "")
    .replace(/\s*(?:–|—|-|\bto\b)\s*/g, "_")
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_+.]/g, "");
  const toCents = (token: string): number | null => {
    const match = /^(\d+(?:\.\d+)?)(k|m)?$/.exec(token);
    if (!match) return null;
    const amount = Number(match[1]);
    const multiplier = match[2] === "m" ? 1_000_000 : match[2] === "k" ? 1_000 : 1;
    return Math.round(amount * multiplier * 100);
  };
  if (normalized.startsWith("under_")) {
    const max = toCents(normalized.slice("under_".length));
    return max == null ? null : { minCents: 0, maxCents: max };
  }
  if (normalized.startsWith("over_")) {
    const min = toCents(normalized.slice("over_".length));
    return min == null ? null : { minCents: min, maxCents: null };
  }
  if (normalized.endsWith("_plus") || normalized.endsWith("+")) {
    const min = toCents(normalized.replace(/_plus$|\+$/, ""));
    return min == null ? null : { minCents: min, maxCents: null };
  }
  const [low, high] = normalized.split("_");
  if (low && high) {
    const min = toCents(low);
    const max = toCents(high);
    if (min != null && max != null) return { minCents: min, maxCents: max };
  }
  return null;
}

export const LEAD_GEN_INDUSTRY_GROUP_KEYS = LEAD_GEN_INDUSTRY_GROUPS.map(
  (group) => group.key,
);

const industryByKey = new Map(
  LEAD_GEN_INDUSTRY_GROUPS.map((group) => [group.key, group]),
);
const industryByIntakeKey = new Map(
  LEAD_GEN_INDUSTRY_GROUPS.map((group) => [group.intakeKey, group]),
);

export function getLeadGenIndustryGroup(key: string) {
  return industryByKey.get(key);
}

export function getLeadGenIndustryGroupByIntakeKey(intakeKey: string) {
  return industryByIntakeKey.get(intakeKey);
}

export function isLeadGenIndustryGroupKey(value: unknown): value is string {
  return typeof value === "string" && industryByKey.has(value);
}
